import { useComputed } from "@suse-o11y/signal";
import React from "react";

import { Checkbox } from "~/components/checkbox";
import { toCheckState } from "~/components/checkbox/state";
import { Table } from "~/components/table";
import { TableBody } from "~/components/table/body";
import { TableCell } from "~/components/table/cell";
import { TableColOrder, type TableColOrderIterate } from "~/components/table/col-order";
import { TableHead } from "~/components/table/head";
import { TableHeadCell } from "~/components/table/head-cell";
import { TableHeadCellSorter } from "~/components/table/head-cell-sorter";
import { TableRow } from "~/components/table/row";
import { Truncated } from "~/components/truncated";
import { formatUnits } from "~/tools/formatter-unit";
import { prop } from "~/tools/prop";
import { times } from "~/tools/times";

import type { ChartDataPoint } from "../_create-chart-data-points";

import {
	type LegendEntry,
	type LegendEntryWithCalculatedValues,
	toLegendEntryWithCalculatedValues,
} from "./_legend-entry";
import { LegendEntryLabel } from "./_legend-entry-label";
import type { LegendTableCalculatedValueColumn } from "./_legend-table-calculated-value-column";
import type { State } from "./_state";

const NAME_COLUMN_LABEL = "Name";

export interface LegendContentTableProps {
	unit: string;
	entries: ReadonlyArray<LegendEntry>;
	points: ReadonlyArray<ChartDataPoint>;
	columns: ReadonlyArray<LegendTableCalculatedValueColumn>;
	state: State;
}

export const LegendContentTable: React.FC<LegendContentTableProps> = ({ unit, entries, points, columns, state }) => {
	const namespace = React.useId();

	const tableColumnsOrder = useComputed(state.tableColumnsOrder);

	// enrich the entries with the calculated values separately from the sorting
	// to avoid recalculating them on every sort change
	const entriesWithColumns = React.useMemo(
		() => entries.map((entry) => toLegendEntryWithCalculatedValues(entry, points, columns)),
		[columns, entries, points],
	);

	const sortedEntries = React.useMemo(() => {
		return TableColOrder.orderBy(
			entriesWithColumns,

			// the Name column is always defined
			[prop("label"), tableColumnsOrder.get(NAME_COLUMN_LABEL)],

			// the columns are dynamically defined, so we need to map them
			...columns.map(
				({ label }): TableColOrderIterate<LegendEntryWithCalculatedValues> => [
					({ calculated }) => calculated.get(label),
					tableColumnsOrder.get(label),
				],
			),
		);
	}, [entriesWithColumns, tableColumnsOrder, columns]);

	const masterCheckboxState = React.useMemo(
		() => toCheckState(entriesWithColumns.map(prop("active"))),
		[entriesWithColumns],
	);

	return (
		<Table
			paddings={["cell-xs"]}
			style={{
				gridTemplateColumns: ["auto", "1fr", ...times(columns.length, () => "auto")].join(" "),
			}}
			text="xs"
		>
			<TableHead className="sticky top-0">
				<TableRow>
					<TableHeadCell className="items-center">
						<Checkbox
							checked={masterCheckboxState}
							onCheckedChange={(selected) => state.setAllLabels(selected)}
							size="sm"
						/>
					</TableHeadCell>

					<TableHeadCell className="items-center">
						{NAME_COLUMN_LABEL}
						<TableHeadCellSorter
							onOrderChange={(order) =>
								state.tableColumnsOrder.write((current) => current.set(NAME_COLUMN_LABEL, order))
							}
							order={tableColumnsOrder.get(NAME_COLUMN_LABEL)}
						/>
					</TableHeadCell>

					{columns.map(({ label }) => (
						<TableHeadCell className="items-center justify-end" key={label}>
							{label}
							<TableHeadCellSorter
								onOrderChange={(order) => state.tableColumnsOrder.write((current) => current.set(label, order))}
								order={tableColumnsOrder.get(label)}
							/>
						</TableHeadCell>
					))}
				</TableRow>
			</TableHead>

			<TableBody each={sortedEntries}>
				{(entry) => (
					<LegendContentTableRow
						columns={columns}
						key={entry.index}
						namespace={namespace}
						state={state}
						unit={unit}
						{...entry}
					/>
				)}
			</TableBody>
		</Table>
	);
};

const LegendContentTableRow: React.FC<
	LegendEntryWithCalculatedValues & {
		unit: string;
		namespace: string;
		columns: ReadonlyArray<LegendTableCalculatedValueColumn>;
		state: State;
	}
> = ({ unit, namespace, index, label, active, color, componentIdentifier, columns, calculated, state }) => (
	<TableRow className={active ? undefined : "text-grey-500"}>
		<TableCell>
			<Checkbox
				checked={active}
				id={`${namespace}-${index}`}
				onCheckedChange={(checked) => state.setLabel(label, checked)}
				size="sm"
			/>
		</TableCell>

		{/* needs the overflow to prevent the content causing the table's false-scroll state */}
		<LegendEntryLabel
			className="items-center overflow-hidden"
			color={color}
			componentIdentifier={componentIdentifier}
			el={TableCell}
			label={label}
		>
			{/* flex-1 pushes the truncated content so it always tries to fill the whole available space */}
			<Truncated
				className="flex-1" // force re-render on columns change so the truncation is recalculated
				el="label"
				htmlFor={`${namespace}-${index}`}
				key={columns.length}
			>
				{label}
			</Truncated>
		</LegendEntryLabel>

		{columns.map((column) => {
			const value = calculated.get(column.label);

			return (
				<TableCell className="justify-end" key={column.label}>
					{value == null ? "-" : formatUnits(unit, value)}
				</TableCell>
			);
		})}
	</TableRow>
);
